import { useToast } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { createId } from '@paralleldrive/cuid2';
import { t } from '@lingui/core/macro';
import { useFilesTrpcClient } from '@duke-hq/svc-files-ui/FilesTrpcClientProvider';
import { uploadFileImperative } from '@duke-hq/svc-files-ui/uploadFileImperative';
import type { UploadedDoc } from './types';

export type EmployerDocKind =
  | 'EMPLOYER_AGREEMENT'
  | 'EMPLOYER_INSURANCE'
  | 'WORK_PLACEMENT_AGREEMENT';

// Uploads a single employer compliance PDF and returns its DRN. Runs before the
// employer exists, so no attachedToId is passed (the files service generates
// the S3 key). Stored private; the display keeps the user's original filename
// while the file itself is renamed to an ASCII-safe CUID (the DRN travels as an
// S3 x-amz-meta-drn header, which rejects non-Latin-1 characters).
export function useEmployerDocUpload(attachedToKind: EmployerDocKind) {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { trpcClient: filesTrpcClient, initialize } = useFilesTrpcClient();

  const uploadFile = useCallback(
    async (file: File): Promise<UploadedDoc> => {
      try {
        setIsLoading(true);
        const extension = file.name.split('.').pop();
        if (extension == null || extension === file.name) {
          throw new Error('File extension is missing');
        }
        const renamed = new File([file], `${createId()}.${extension}`, { type: file.type });

        await initialize();
        const result = await uploadFileImperative({
          file: renamed,
          actions: {
            createFiles: (input) => filesTrpcClient.file.createFiles.mutate(input),
            createPartUploadUrl: (input) => filesTrpcClient.file.createPartUploadUrl.mutate(input),
            completeFileUpload: (input) => filesTrpcClient.file.completeFileUpload.mutate(input),
            cancelFileUpload: (input) => filesTrpcClient.file.cancelFileUpload.mutate(input),
          },
          attachedToKind,
          visibility: 'private',
        });
        return { filename: file.name, drn: result.fileDrn };
      } catch (error) {
        toast({
          status: 'error',
          title: t`Upload failed`,
          description: error instanceof Error ? error.message : undefined,
        });
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [attachedToKind, filesTrpcClient, initialize, toast],
  );

  return { uploadFile, isLoading };
}
